import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../../api/api';

const API_BRANCH = `${API_URL}/branches`;
const API_DEALER = `${API_URL}/dealers`;

const AddDealerLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [branches, setBranches] = useState([]);
    const [form, setForm] = useState({
        storename: '',
        ownername: '',
        userfile: null,
        storeid: '',
        email: '',
        mobile: '',
        city: '',
        address: '',
        pincode: '',
        branchname: ''
    });
    const [existingImage, setExistingImage] = useState('');
    const token = localStorage.getItem('token');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Fetch branches
    useEffect(() => {
        axios.get(API_BRANCH)
            .then(res => setBranches(res.data))
            .catch(err => console.error("Failed to fetch branches:", err));
    }, []);

    // If in update mode, fetch existing dealer
    useEffect(() => {
        if (id) {
            axios.get(`${API_DEALER}/${id}`)
                .then(res => {
                    const dealer = res.data;
                    setForm({
                        storename: dealer.storename,
                        ownername: dealer.ownername,
                        userfile: null,
                        storeid: dealer.storeid || '',
                        email: dealer.email,
                        mobile: dealer.mobile,
                        city: dealer.city,
                        address: dealer.address,
                        pincode: dealer.pincode,
                        branchname: dealer.branchname
                    });
                    setExistingImage(dealer.userfile); // for preview if needed
                })
                .catch(err => {
                    console.error("Failed to fetch dealer:", err);
                    Swal.fire("Error", "Dealer not found", "error");
                    navigate(-1);
                });
        }
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'file' ? files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const requiredFields = ['storename', 'ownername', 'email', 'mobile', 'city', 'address', 'pincode', 'branchname'];
        if (!id) requiredFields.push('userfile'); // image is required only when adding

        for (const field of requiredFields) {
            if (!form[field]) {
                Swal.fire("Validation Error", `Field "${field}" is required`, "error");
                return;
            }
        }

        try {
            const formData = new FormData();
            for (const key in form) {
                if (form[key] !== null) {
                    formData.append(key, form[key]);
                }
            }

            if (id) {
                await axios.post(`${API_DEALER}/${id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                Swal.fire("Success", "Dealer updated successfully", "success");
            } else {
                await axios.post(API_DEALER, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                Swal.fire("Success", "Dealer added successfully", "success");
            }

            navigate(-1);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to submit form", "error");
        }
    };

    return (
        <div className="row gy-4">
            <div className="col-md-12">
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">{id ? 'Update Dealer' : 'Add Dealer'}</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div style={{ background: "#FFF", padding: "10px", border: "thin groove rgba(244,244,244,0.3)" }}>
                                <div className="row">
                                    {/* Left Side */}
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Shop Name</label>
                                            <input type="text" className="form-control" name="storename" value={form.storename} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Owner Name</label>
                                            <input type="text" className="form-control" name="ownername" value={form.ownername} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Image {existingImage && `(Leave empty to keep current)`}</label>
                                            <input type="file" className="form-control" name="userfile" onChange={handleChange} />
                                            {existingImage && <div><small>Current Image: <a href={`${API_URL}/storage/${existingImage}`} target="_blank" rel="noreferrer">View</a></small></div>}
                                        </div>
                                        <div className="form-group">
                                            <label>Store ID</label>
                                            <input type="text" className="form-control" name="storeid" value={form.storeid} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Email</label>
                                            <input type="email" className="form-control" name="email" value={form.email} onChange={handleChange} required />
                                        </div>
                                    </div>

                                    {/* Right Side */}
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Mobile</label>
                                            <input type="number" className="form-control" name="mobile" value={form.mobile} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>City</label>
                                            <input type="text" className="form-control" name="city" value={form.city} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Address</label>
                                            <textarea className="form-control" name="address" value={form.address} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Pincode</label>
                                            <input type="text" className="form-control" name="pincode" value={form.pincode} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Branch</label>
                                            <select className="form-control" name="branchname" value={form.branchname} onChange={handleChange} required>
                                                <option value="">Select Branch</option>
                                                {branches.map(branch => (
                                                    <option key={branch.id} value={branch.id}>
                                                        {branch.branchname}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 text-end">
                                    <input type="submit" className="btn btn-info" value={id ? "Update" : "Submit"} />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddDealerLayer;
